import { useState, useContext, useRef, useEffect } from "react";
import { Circle, Clock, MinusCircle, EyeOff, ChevronDown } from "lucide-react";
import { AuthContext } from "../App";

const STATUS_OPTIONS = [
  { value: "Available", icon: Circle, color: "text-green-500 fill-green-500", label: "Available" },
  { value: "Away", icon: Clock, color: "text-yellow-500", label: "Away" },
  { value: "Busy", icon: MinusCircle, color: "text-red-500", label: "Busy" },
  { value: "Invisible", icon: EyeOff, color: "text-gray-500", label: "Invisible" },
];

const StatusSelector = () => {
  const { user, updateStatus } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentStatus = STATUS_OPTIONS.find(s => s.value === user?.status_message) || STATUS_OPTIONS[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStatusChange = async (status) => {
    await updateStatus(status.value);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="status-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="retro-button flex items-center gap-2 text-xs px-3 py-1"
        data-testid="status-selector-btn"
      >
        <currentStatus.icon className={`w-3 h-3 ${currentStatus.color}`} />
        <span className="font-arial">{currentStatus.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-1 bg-white retro-window z-50 min-w-[140px]"
          data-testid="status-dropdown"
        >
          <div className="retro-title-bar text-[10px] py-0.5">
            <span>Set Status</span>
          </div>
          <div className="p-1">
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status)}
                className={`w-full flex items-center gap-2 p-2 hover:bg-[#E0E0E0] ${
                  currentStatus.value === status.value ? 'bg-[#D0D0FF]' : ''
                }`}
                data-testid={`status-option-${status.value.toLowerCase()}`}
              >
                <status.icon className={`w-3 h-3 ${status.color}`} />
                <span className="font-arial text-xs">{status.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusSelector;
